/**
 * Svenska jakttider — läns- och positionsmedveten legalitetsbedömning.
 *
 * Källa: Jägareförbundets jakttidsdatabas (jagareforbundet.se/jakt/jakttider/),
 * Naturvårdsverket och jaktförordningen (SFS 2021:334).
 *
 * Notera: Älg och licensvilt fastställs per jaktlag/länsstyrelse och kan skilja sig
 * från riksnormen. Användaren ska alltid kontrollera mot gällande beslut.
 *
 * @version 2.0 — Länsspecifik, subcategori-medveten, positionsbaserad.
 */

// ─── Minimaltyper ────────────────────────────────────────────────────────────

/** Minsta fält från avskjutningsplan (undviker importcykel). */
export type PlanQuotaRow = { gameType: string; plannedCount: number; actualCount: number };

export type LegalityDetail = {
  legal: boolean;
  /** Kort för tooltip / hjälptext */
  summary: string;
  inSeason: boolean;
  inDaylight: boolean;
  inPlan: boolean;
  remaining: number;
  /** Varför det inte är lovligt, om relevant */
  reasons: string[];
};

// ─── Länsmappning ────────────────────────────────────────────────────────────

/**
 * Normaliserar länssträngen (t.ex. "Norrbottens län" → "norrbotten").
 * Hanterar både "Norrbotten", "Norrbottens län", "BD" osv.
 */
function normCounty(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw
    .toLowerCase()
    .replace(/s\s*län\s*$/, '')   // "norrbottens län" → "norrbotten"
    .replace(/\s*län\s*$/, '')    // "norrbotten län" → "norrbotten"
    .trim()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');     // ta bort diakriter
}

/** Hjälp: kontrollera om länssträngen matchar ett av alternativen. */
function countyIs(county: string, ...names: string[]): boolean {
  const c = normCounty(county);
  return names.some(n => c === normCounty(n) || c.includes(normCounty(n)));
}

const NORRLAND_COUNTIES = ['norrbotten', 'vasterbotten', 'jamtland', 'vasternorrland', 'gavleborg'];
const SVEALAND_COUNTIES = ['dalarna', 'varmland', 'orebro', 'vastmanland', 'stockholm', 'uppsala', 'sodermanland'];
const GOTALAND_COUNTIES = ['skane', 'blekinge', 'halland', 'vastra gotaland', 'ostergotland', 'jonkoping', 'kronoberg', 'kalmar', 'gotland'];
const BEAR_COUNTIES = ['norrbotten', 'vasterbotten', 'jamtland', 'vasternorrland', 'gavleborg', 'dalarna', 'varmland', 'gavleborg', 'uppsala'];

function isNorrland(county: string): boolean {
  const c = normCounty(county);
  return NORRLAND_COUNTIES.some(n => c.includes(n));
}

function isBearCounty(county: string): boolean {
  const c = normCounty(county);
  return BEAR_COUNTIES.some(n => c.includes(n));
}

function isSkane(county: string): boolean {
  return normCounty(county).includes('skane');
}

// ─── Datumfönsterhjälpare ────────────────────────────────────────────────────

/** Fönster inom samma kalenderår (start ≤ slut). */
function inMdWindowSameYear(
  now: Date, startM: number, startD: number, endM: number, endD: number
): boolean {
  const m = now.getMonth() + 1;
  const d = now.getDate();
  const afterStart = m > startM || (m === startM && d >= startD);
  const beforeEnd = m < endM || (m === endM && d <= endD);
  return afterStart && beforeEnd;
}

/** Fönster över årsskifte (t.ex. aug–jan). */
function inMdWindowWrapYear(
  now: Date, startM: number, startD: number, endM: number, endD: number
): boolean {
  const m = now.getMonth() + 1;
  const d = now.getDate();
  const afterStart = m > startM || (m === startM && d >= startD);
  const beforeEnd = m < endM || (m === endM && d <= endD);
  return afterStart || beforeEnd;
}

// ─── Normalisering ───────────────────────────────────────────────────────────

function normSpecies(s: string | null | undefined): string {
  if (s == null) return '';
  return String(s)
    .replace(/[﻿​-‍]/g, '')
    .trim()
    .toLowerCase()
    .normalize('NFKC')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/\s+/g, ' ');
}

// ─── Familjenyckel ───────────────────────────────────────────────────────────

/**
 * Mappar viltslags-sträng (inklusive underkategorier) till familjenyckel.
 * T.ex. "Råbock medalj" → "radjur", "Kapitalhjort" → "kronhjort".
 */
export function planSpeciesFamilyKey(raw: string): string {
  const s = normSpecies(raw);
  if (
    s.includes('vildsvin') || s === 'svin' || s === 'galt' || s.includes('vildgalt') ||
    s === 'sugga' || s.includes('vildsugga') || s.includes('kultinge') ||
    s.includes('kulting') || s.includes('vildgris') || s.includes('smgris') ||
    s.includes('smagris') || s.includes('arsgris') || s === 'gris'
  ) return 'vildsvin';
  if (s.includes('mufflon')) return 'mufflon';
  if (s.includes('dovhjort') || s.includes('davhjort') || s.includes('fallow') || /\bdov/.test(s)) return 'dovhjort';
  if (s.includes('kronhjort') || s.startsWith('kron') || s === 'hjort') return 'kronhjort';
  if (
    s.includes('radjur') || s.includes('rakid') || s.includes('raget') ||
    s.includes('rabock') || s.includes('smaldjur')
  ) return 'radjur';
  if (s.includes('alg')) return 'alg';
  if (s.includes('bjorn')) return 'bjorn';
  if (s.includes('hare')) return 'hare';
  if (s.includes('fasan')) return 'fasan';
  if (s.includes('orre')) return 'orre';
  if (s.includes('tjader')) return 'tjader';
  if (s.includes('skogsfagel') || s.includes('skogsf')) return 'skogsfagel';
  if (s.includes('kanadagas') || s.includes('gragas') || s === 'gass' || s === 'gas' || s === 'gäss') return 'kanadagas';
  if (s === 'and' || s.includes('andfagel') || s.includes('grasand') || s.includes('kricka') || s.includes('blasand')) return 'andfagel';
  if (s.includes('gravling')) return 'gravling';
  if (s.includes('rav')) return 'rav';
  if (s.includes('baver')) return 'baver';
  if (s.includes('mardhund') || s.includes('mardh')) return 'mardhund';
  if (s.includes('mink')) return 'mink';
  if (s.includes('mard')) return 'mard';
  return s;
}

// ─── Subkategori → kön/ålder ─────────────────────────────────────────────────

type Gender = 'hane' | 'hona' | 'any';
type AgeClass = 'vuxen' | 'ung' | 'any';

interface AnimalClass {
  gender: Gender;
  ageClass: AgeClass;
}

/**
 * Härleder kön och åldersklass från underkategorinamnet.
 * Används för att avgöra om *just denna subkategori* är lovlig,
 * t.ex. råbock när det är råbockstid men inte råget-tid.
 */
export function subcatToAnimalClass(raw: string): AnimalClass {
  const s = normSpecies(raw);

  // Rådjur
  if (s.includes('rabock')) return { gender: 'hane', ageClass: 'vuxen' };
  if (s.includes('raget'))  return { gender: 'hona', ageClass: 'vuxen' };
  if (s.includes('rakid'))  return { gender: 'any',  ageClass: 'ung' };

  // Älg
  if (s.includes('alg') && (s.includes('tjur') || s.includes('oxe'))) return { gender: 'hane', ageClass: 'vuxen' };
  if (s.includes('alg') && s.includes('kviga')) return { gender: 'hona', ageClass: 'ung' };
  if (s.includes('alg') && s.includes('ko'))    return { gender: 'hona', ageClass: 'vuxen' };
  if (s.includes('alg') && s.includes('kalv'))  return { gender: 'any',  ageClass: 'ung' };
  if (s === 'kalv') return { gender: 'any', ageClass: 'ung' };

  // Kronhjort
  if (s.includes('kron') && s.includes('kalv'))  return { gender: 'any',  ageClass: 'ung' };
  if (s.includes('kron') && s.includes('hind'))  return { gender: 'hona', ageClass: 'vuxen' };
  if (s.includes('spets') && !s.includes('dov')) return { gender: 'hane', ageClass: 'ung' };  // spetshjort = yearling
  if (
    s.includes('kapitalhjort') || s.includes('kapital') ||
    (s.includes('hjort') && (s.includes('tagg') || s.includes('u10') || s.includes('under')))
  ) return { gender: 'hane', ageClass: 'vuxen' };

  // Dovhjort
  if (s.includes('dov') && s.includes('kalv')) return { gender: 'any',  ageClass: 'ung' };
  if (s.includes('dov') && s.includes('hind')) return { gender: 'hona', ageClass: 'vuxen' };
  if (s.includes('dov') && (s.includes('skovel') || s.includes('spets'))) return { gender: 'hane', ageClass: 'vuxen' };

  // Vildsvin
  if (s === 'sugga' || s.includes('vildsugga')) return { gender: 'hona', ageClass: 'vuxen' };
  if (s === 'galt'  || s.includes('vildgalt'))  return { gender: 'hane', ageClass: 'vuxen' };
  if (s.includes('arsgris') || s.includes('kultinge') || s.includes('kulting')) return { gender: 'any', ageClass: 'ung' };

  // Mufflon
  if (s.includes('bagge'))                          return { gender: 'hane', ageClass: 'vuxen' };
  if (s.includes('muf') && s.includes('hona'))      return { gender: 'hona', ageClass: 'vuxen' };
  if (s.includes('lamm'))                           return { gender: 'any',  ageClass: 'ung' };

  // Fåglar — tupp/hane/hona
  if (s.includes('tupp') || s.includes('hane')) return { gender: 'hane', ageClass: 'any' };
  if (s.includes('hona'))                       return { gender: 'hona', ageClass: 'any' };

  return { gender: 'any', ageClass: 'any' };
}

// ─── Säsongsregler med länsmedvetenhet ──────────────────────────────────────

/**
 * Returnerar true om just denna kombination av viltslag + underkategori
 * är inom jaktsäsong för angiven plats och tidpunkt.
 *
 * Baserat på SFS 2021:334 och Jägareförbundets jakttidsdatabas.
 */
function inSeasonForLocation(
  gameType: string,
  now: Date,
  county: string,
): boolean {
  const family = planSpeciesFamilyKey(gameType);
  const animalClass = subcatToAnimalClass(gameType);

  switch (family) {

    // ── Vildsvin: helårsjakt ──────────────────────────────────────────────────
    case 'vildsvin':
      return true;

    // ── Rådjur ───────────────────────────────────────────────────────────────
    case 'radjur': {
      const { gender, ageClass } = animalClass;
      // Råbock (hane): 1 maj–31 jul  +  16 aug–31 jan
      if (gender === 'hane' || normSpecies(gameType).includes('rabock')) {
        return (
          inMdWindowSameYear(now, 5, 1, 7, 31) ||
          inMdWindowWrapYear(now, 8, 16, 1, 31)
        );
      }
      // Råget och Råkid (hona/ung): 1 okt–31 jan
      if (gender === 'hona' || ageClass === 'ung' ||
          normSpecies(gameType).includes('raget') || normSpecies(gameType).includes('rakid')) {
        return inMdWindowWrapYear(now, 10, 1, 1, 31);
      }
      // Generisk "rådjur": hela rådjursfamiljen (inkl. bock-tid)
      return (
        inMdWindowSameYear(now, 5, 1, 7, 31) ||
        inMdWindowWrapYear(now, 8, 16, 1, 31)
      );
    }

    // ── Älg ──────────────────────────────────────────────────────────────────
    case 'alg':
      // Licensjakt: bestäms per jaktlag, riksnorm ca 15 sep–31 jan
      return inMdWindowWrapYear(now, 9, 15, 1, 31);

    // ── Kronhjort ────────────────────────────────────────────────────────────
    case 'kronhjort': {
      const { gender, ageClass } = animalClass;
      if (isSkane(county)) {
        // Skåne: licensjakt 8 okt–31 jan (alla kategorier)
        return inMdWindowWrapYear(now, 10, 8, 1, 31);
      }
      // Inom förvaltningsområde:
      //   16 aug–7 okt: hind + kalv (smyg-/vaktjakt)
      //   8 okt–31 jan: alla
      //   1 feb–28 feb: hind + kalv
      if (gender === 'hona' || ageClass === 'ung') {
        return (
          inMdWindowSameYear(now, 8, 16, 10, 7) ||
          inMdWindowWrapYear(now, 10, 8, 1, 31) ||
          inMdWindowSameYear(now, 2, 1, 2, 28)
        );
      }
      // Hane (hjort, kapitalhjort): 8 okt–31 jan
      if (gender === 'hane') {
        return inMdWindowWrapYear(now, 10, 8, 1, 31);
      }
      // Spetshjort (yearling hane): utanför förv.omr.: 16 aug–31 jan
      if (normSpecies(gameType).includes('spets')) {
        return inMdWindowWrapYear(now, 8, 16, 1, 31);
      }
      // Generisk kronhjort
      return inMdWindowWrapYear(now, 9, 1, 1, 31);
    }

    // ── Dovhjort ─────────────────────────────────────────────────────────────
    case 'dovhjort':
      // Hind+kalv: 1 sep–31 jan; Hjort/skovel: 1 okt–31 jan
      if (animalClass.gender === 'hona' || animalClass.ageClass === 'ung') {
        return inMdWindowWrapYear(now, 9, 1, 1, 31);
      }
      return inMdWindowWrapYear(now, 10, 1, 1, 31);

    // ── Björn ─────────────────────────────────────────────────────────────────
    case 'bjorn':
      if (!isBearCounty(county)) return false; // Inga björnar i Skåne etc.
      return inMdWindowSameYear(now, 8, 21, 10, 15);

    // ── Mufflon ───────────────────────────────────────────────────────────────
    case 'mufflon':
      return inMdWindowWrapYear(now, 9, 1, 1, 31);

    // ── Hare ─────────────────────────────────────────────────────────────────
    case 'hare':
      if (isNorrland(county)) {
        return inMdWindowWrapYear(now, 8, 15, 3, 31); // längre i norr
      }
      return inMdWindowWrapYear(now, 8, 15, 2, 28);

    // ── Fasan ─────────────────────────────────────────────────────────────────
    case 'fasan': {
      const s = normSpecies(gameType);
      // Tupp: 1 okt–28 feb; Höna: 1 okt–31 jan
      if (s.includes('hona') || animalClass.gender === 'hona') {
        return inMdWindowWrapYear(now, 10, 1, 1, 31);
      }
      return inMdWindowWrapYear(now, 10, 1, 2, 28);
    }

    // ── Orre ──────────────────────────────────────────────────────────────────
    case 'orre':
      // Orrtuppar: 25 aug–15 nov (riksnorm); norra Sverige kan ha avvikelse
      return inMdWindowSameYear(now, 8, 25, 11, 15);

    // ── Tjäder ────────────────────────────────────────────────────────────────
    case 'tjader':
      // Tjädertuppar: 25 aug–15 nov
      return inMdWindowSameYear(now, 8, 25, 11, 15);

    // ── Skogsfågel (generisk) ─────────────────────────────────────────────────
    case 'skogsfagel':
      return inMdWindowSameYear(now, 8, 25, 11, 15);

    // ── Kanadagås / Grågås ────────────────────────────────────────────────────
    case 'kanadagas':
      // 20 aug–15 mar (riksnorm)
      return inMdWindowWrapYear(now, 8, 20, 3, 15);

    // ── Andfågel ──────────────────────────────────────────────────────────────
    case 'andfagel':
      return inMdWindowWrapYear(now, 8, 21, 1, 31);

    // ── Grävling ──────────────────────────────────────────────────────────────
    case 'gravling':
      return inMdWindowWrapYear(now, 8, 1, 1, 31);

    // ── Räv ───────────────────────────────────────────────────────────────────
    case 'rav':
      return inMdWindowWrapYear(now, 8, 21, 2, 28);

    // ── Bäver ─────────────────────────────────────────────────────────────────
    case 'baver':
      return inMdWindowWrapYear(now, 10, 1, 5, 31);

    // ── Helårsjakt (invasivt/skydd) ───────────────────────────────────────────
    case 'mink':
    case 'mardhund':
    case 'mard':
      return true;

    default:
      return false;
  }
}

// ─── Dygnsljus ───────────────────────────────────────────────────────────────

/**
 * Kontrollerar om jakten är tillåten vid denna tidpunkt på dygnet.
 * Standard klövvilt: 1h före soluppgång – 1h efter solnedgång.
 * Björn (striktare): 1h före soluppgång – 2h FÖRE solnedgång.
 * Vildsvin/mink/mårdhund: inga dygnsrestriktioner.
 */
export function isWithinHuntingDaylight(
  now: Date,
  sunriseIso?: string | null,
  sunsetIso?: string | null,
  family?: string,
): boolean {
  // Utan soltid: blockera inte — väntar på Open-Meteo/position
  if (!sunriseIso || !sunsetIso) return true;

  const sr = parseIsoLocal(sunriseIso);
  const ss = parseIsoLocal(sunsetIso);
  if (!sr || !ss) return true;

  const t = now.getTime();

  // Björn: sluta 2h FÖRE solnedgång
  if (family === 'bjorn') {
    const start = sr.getTime() - 60 * 60 * 1000;          // 1h före SR
    const end   = ss.getTime() - 2 * 60 * 60 * 1000;      // 2h före SS
    return t >= start && t <= end;
  }

  // Standard: 1h före SR – 1h efter SS
  const start = sr.getTime() - 60 * 60 * 1000;
  const end   = ss.getTime() + 60 * 60 * 1000;
  return t >= start && t <= end;
}

/** Viltslag som får jagas dygnet runt. */
function hasNoDaylightRestriction(gameType: string): boolean {
  const family = planSpeciesFamilyKey(gameType);
  return family === 'vildsvin' || family === 'mink' || family === 'mardhund' || family === 'mard';
}

function parseIsoLocal(iso: string): Date | null {
  try {
    const t = Date.parse(iso);
    return Number.isNaN(t) ? null : new Date(t);
  } catch {
    return null;
  }
}

// ─── Kvotberäkning ───────────────────────────────────────────────────────────

/** Återstående att skjuta för ett viltslags familj. */
export function planRemainingForGameType(plan: PlanQuotaRow[], gameType: string): number {
  const wantKey = planSpeciesFamilyKey(gameType);
  let sum = 0;
  for (const p of plan) {
    if (planSpeciesFamilyKey(p.gameType) !== wantKey) continue;
    const pPlanned = Number.isFinite(Number(p.plannedCount)) ? Number(p.plannedCount) : 0;
    const pActual  = Number.isFinite(Number(p.actualCount))  ? Number(p.actualCount)  : 0;
    sum += Math.max(0, pPlanned - pActual);
  }
  return sum;
}

// ─── Huvud-API ───────────────────────────────────────────────────────────────

/**
 * Grön indikator om: säsongsregel + dygnsljus OK, och om marken har
 * avskjutningsplan ska det finnas kvar i planen för det viltslaget.
 *
 * @param args.county     Markens län (t.ex. "Södermanlands län", "Skåne").
 *                        Används för länsspecifika jakttider (björn, kronhjort i Skåne, etc.)
 * @param args.mapLat     Markens latitud — reserverat för framtida kust/inland-zoner.
 * @param args.mapLng     Markens longitud.
 */
export function evaluateShootingLegality(args: {
  gameType: string;
  now: Date;
  /** Om längd > 0 krävs även kvar i plan för detta slag */
  shootingPlan: PlanQuotaRow[];
  sunriseIso?: string | null;
  sunsetIso?: string | null;
  /** Markens län — påverkar länsspecifika jakttider */
  county?: string | null;
  /** Markens GPS-position (reserverat för kust/inland-logik) */
  mapLat?: number | null;
  mapLng?: number | null;
}): LegalityDetail {
  const { gameType, now, shootingPlan, sunriseIso, sunsetIso } = args;
  const county = args.county ?? '';
  const family = planSpeciesFamilyKey(gameType);

  const seasonOk  = inSeasonForLocation(gameType, now, county);
  const noDayRestriction = hasNoDaylightRestriction(gameType);
  const daylightOk = noDayRestriction || isWithinHuntingDaylight(now, sunriseIso, sunsetIso, family);

  const inPlan = shootingPlan.some(p =>
    planSpeciesFamilyKey(p.gameType) === planSpeciesFamilyKey(gameType) &&
    Number(p.plannedCount) > 0
  );
  const remaining = planRemainingForGameType(shootingPlan, gameType);
  const quotaOk = !inPlan || remaining > 0;

  const reasons: string[] = [];
  if (!seasonOk)            reasons.push('utanför jakttid');
  if (!daylightOk)          reasons.push('fel tid på dygnet');
  if (inPlan && remaining <= 0) reasons.push('kvot slut');

  const legal = seasonOk && daylightOk && quotaOk;

  let summary: string;
  if (legal) {
    summary = inPlan
      ? `Jaktbar — ${remaining} kvar i plan`
      : 'Jaktbar (ingen kvotplan)';
  } else {
    summary = `Ej lovligt: ${reasons.join(', ')}`;
  }

  return { legal, summary, inSeason: seasonOk, inDaylight: daylightOk, inPlan, remaining, reasons };
}
